// useIntlAccaHub — tournament header + groups + matches + bracket for
// the 4-tab Tournament Hub. The match/standings tables are a future
// C-phase migration (intl_acca_matches, intl_acca_group_standings).
//
// DEFENSIVE / HONEST-EMPTY: those tables DO NOT EXIST yet. Every query
// against them is caught and degraded to empty so the page renders its
// "Awaiting data" honest-empty state. NEVER throw, NEVER fabricate.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import type { IntlAccaTournament } from "../db/intl-acca-types.ts";
import type {
  GroupStanding,
  IntlAccaMatch,
  Nation,
} from "../db/intl-acca-types-extended.ts";
import type { BracketState } from "../pages/intl-acca/TournamentHubPage.tsx";

const EMPTY_BRACKET: BracketState = { r32: [], r16: [], qf: [], sf: [], final: [] };

export interface IntlAccaHubVM {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  groups: GroupStanding[];
  matches: IntlAccaMatch[];
  nations: Record<string, Nation>;
  participatingNations: string[];
  bracket: BracketState;
  refetch: () => Promise<void>;
}

/** Knockout rows → bracket buckets. Placeholders stay null ("TBD"). */
function computeBracket(matches: IntlAccaMatch[]): BracketState {
  const pick = (round: IntlAccaMatch["knockout_round"]) =>
    matches
      .filter((m) => m.knockout_round === round)
      .sort((x, y) => (x.kickoff ?? "").localeCompare(y.kickoff ?? ""))
      .map((m) => ({
        a: m.home || null,
        b: m.away || null,
        a_slot: m.home_slot ?? null,
        b_slot: m.away_slot ?? null,
        is_final: round === "FINAL" ? true : undefined,
      }));
  return {
    r32: pick("R32"),
    r16: pick("R16"),
    qf: pick("QF"),
    sf: pick("SF"),
    final: pick("FINAL"),
  };
}

export function useIntlAccaHub(tournamentId: string): IntlAccaHubVM {
  const supabase = useMemo(() => getBrowserClient() as any, []);
  const [s, setS] = useState<Omit<IntlAccaHubVM, "refetch">>({
    loading: true, error: null, tournament: null, groups: [],
    matches: [], nations: {}, participatingNations: [],
    bracket: EMPTY_BRACKET,
  });

  const fetchAll = useCallback(async () => {
    if (!tournamentId) {
      setS({
        loading: false, error: null, tournament: null, groups: [],
        matches: [], nations: {}, participatingNations: [],
        bracket: EMPTY_BRACKET,
      });
      return;
    }
    setS((p) => ({ ...p, loading: true, error: null }));

    // The tournament table is real.
    let tournament: IntlAccaTournament | null = null;
    try {
      const tRes = await supabase.from("intl_acca_tournaments")
        .select("*").eq("id", tournamentId).maybeSingle();
      if (tRes.error) throw tRes.error;
      tournament = (tRes.data as IntlAccaTournament | null) ?? null;
    } catch {
      tournament = null;
    }

    // C-phase tables — may not exist. Catch each independently and
    // degrade to empty so the page renders its honest-empty state.
    let matches: IntlAccaMatch[] = [];
    try {
      const mRes = await supabase.from("intl_acca_matches")
        .select("*").eq("tournament_id", tournamentId);
      if (!mRes.error) matches = (mRes.data ?? []) as IntlAccaMatch[];
    } catch {
      matches = [];
    }

    let groups: GroupStanding[] = [];
    try {
      const gRes = await supabase.from("intl_acca_group_standings")
        .select("*").eq("tournament_id", tournamentId);
      if (!gRes.error && Array.isArray(gRes.data)) {
        groups = gRes.data as GroupStanding[];
      }
    } catch {
      groups = [];
    }

    const participatingNations = Array.from(
      new Set(
        matches
          .flatMap((m) => [m.home, m.away])
          .filter((c): c is string => Boolean(c)),
      ),
    );

    // Resolve the nations reference map so NationFlag can render real
    // flag SVGs (NationFlag with nation=null only shows the neutral
    // placeholder). Best-effort: degrade to {} on error/empty.
    let nations: Record<string, Nation> = {};
    try {
      const nRes = await supabase.from("nations").select("*");
      if (!nRes.error && Array.isArray(nRes.data)) {
        nations = Object.fromEntries(
          (nRes.data as Nation[]).map((n) => [n.code, n]),
        );
      }
    } catch {
      nations = {};
    }

    setS({
      loading: false, error: null,
      tournament,
      groups,
      matches,
      nations,
      participatingNations,
      bracket: computeBracket(matches),
    });
  }, [supabase, tournamentId]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);
  return { ...s, refetch: fetchAll };
}
